/* The church's weekly services — Sunday Service at 7, 9 and 11, say.
 *
 * A service is a name, a day of the week, one to six times that day, and
 * who is expected. Changes apply from the next service day, so editing on a
 * Sunday afternoon does not rewrite the morning's roll.
 *
 * Each service opens its own page, with every day it has been held.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { CalendarClock, ChevronRight, Filter, Loader2, Pencil, Plus, Trash2, UserCheck, Users, X } from 'lucide-react'
import { useDeleteService, useGroups, useSaveService, useServices } from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, SelectField } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import type { AudienceFilter, Service, ServiceAudienceKind } from '@/types/api'
import {
  AudienceCount,
  EMPTY_FILTER,
  RuleBuilder,
  completeFilter,
} from '@/features/church/audience/RuleBuilder'
import { WEEKDAYS } from './parts'

/** "09:00" → "9:00 am" */
export function hhmm(t: string): string {
  const [h, m] = t.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m, 0, 0)
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}

export function ServicesManager({ churchId }: { churchId: string }) {
  const services = useServices(churchId)
  const [editing, setEditing] = useState<Service | 'new' | null>(null)
  const [removing, setRemoving] = useState<Service | null>(null)
  const remove = useDeleteService(churchId)

  return (
    <div className="space-y-s3">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" aria-hidden />
          New service
        </Button>
      </div>

      {services.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : services.isError ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          {(services.error as Error).message}
        </Card>
      ) : !services.data?.filter((s) => s.kind === 'weekly').length ? (
        <Card className="p-s8 text-center">
          <CalendarClock className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No services yet</p>
          <p className="mt-1 text-base text-ink-2">
            Add your Sunday service, with each of its times, and members can
            check in to it.
          </p>
        </Card>
      ) : (
        services.data
          .filter((s) => s.kind === 'weekly')
          .map((s) => (
            <Card key={s.id} className="flex items-start gap-s3 p-s4 transition-colors hover:bg-hover">
              <Link to={`/services/${s.id}`} className="group min-w-0 flex-1">
                <h3 className="flex items-center gap-1 text-md font-bold group-hover:text-brand">
                  {s.name}
                  <ChevronRight className="h-4 w-4 text-ink-3" aria-hidden />
                </h3>
                <p className="text-sm text-ink-2">
                  Every {s.weekday !== null ? WEEKDAYS[s.weekday] : 'week'} · {s.audience_label}
                  {s.audience_kind === 'chosen' && s.regulars != null
                    ? ` · ${s.regulars} attend regularly`
                    : ''}
                </p>
                <div className="mt-s2 flex flex-wrap gap-1.5">
                  {s.slots.map((slot) => (
                    <span
                      key={slot.id}
                      className="rounded-pill bg-surface-2 px-s2 py-0.5 text-sm text-ink-2"
                    >
                      {hhmm(slot.starts_time)} {slot.label}
                    </span>
                  ))}
                </div>
              </Link>
              <button
                onClick={() => setEditing(s)}
                aria-label={`Edit ${s.name}`}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-ink-2 hover:bg-hover-2"
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                onClick={() => setRemoving(s)}
                aria-label={`Delete ${s.name}`}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-pill text-ink-2 hover:bg-hover-2 hover:text-danger"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </Card>
          ))
      )}

      {editing ? (
        <ServiceDialog
          churchId={churchId}
          service={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Confirm
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Delete ${removing?.name ?? 'this service'}?`}
        body="It stops from the next service day. Days already held keep their attendance."
        confirmLabel="Delete service"
        loading={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, { onSuccess: () => setRemoving(null) })
        }
      />
    </div>
  )
}

type SlotDraft = { label: string; starts_time: string; ends_time: string }

export function ServiceDialog({
  churchId,
  service,
  onClose,
}: {
  churchId: string
  service: Service | null
  onClose: () => void
}) {
  const save = useSaveService(churchId)
  const groups = useGroups(churchId)
  const [name, setName] = useState(service?.name ?? 'Sunday Service')
  const [weekday, setWeekday] = useState(String(service?.weekday ?? 6))
  const [slots, setSlots] = useState<SlotDraft[]>(
    service?.slots.map(({ label, starts_time, ends_time }) => ({ label, starts_time, ends_time })) ?? [
      { label: 'English', starts_time: '09:00', ends_time: '10:30' },
    ],
  )
  const [audience, setAudience] = useState<ServiceAudienceKind>(service?.audience_kind ?? 'church')
  const [groupId, setGroupId] = useState(service?.group_id ?? '')
  const [rules, setRules] = useState<AudienceFilter>(
    (service?.audience_filter as AudienceFilter | null) ?? EMPTY_FILTER,
  )
  const [error, setError] = useState<string | null>(null)

  const chosenRules = audience === 'filter' ? completeFilter(rules) : null
  const slotsOk = slots.every(
    (s) => s.label.trim() && s.starts_time && s.ends_time && s.starts_time < s.ends_time,
  )
  const ready =
    name.trim().length > 0 &&
    slots.length > 0 &&
    slotsOk &&
    (audience !== 'group' || groupId) &&
    (audience !== 'filter' || chosenRules !== null)

  function setSlot(i: number, patch: Partial<SlotDraft>) {
    setSlots((all) => all.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  }

  function submit() {
    if (!ready || save.isPending) return
    setError(null)
    save.mutate(
      {
        id: service?.id,
        input: {
          name: name.trim(),
          weekday: Number(weekday),
          slots: slots.map((s) => ({ ...s, label: s.label.trim() })),
          audience_kind: audience,
          group_id: audience === 'group' || audience === 'filter' ? groupId || null : null,
          audience_filter: chosenRules,
        },
      },
      {
        onSuccess: onClose,
        onError: (err) => setError((err as Error).message),
      },
    )
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={service ? 'Edit service' : 'New service'}
      width="600px"
      guardClose
      footer={
        <Button variant="primary" size="lg" full onClick={submit} disabled={!ready} loading={save.isPending}>
          {service ? 'Save changes' : 'Add service'}
        </Button>
      }
    >
      <div className="space-y-s4 p-s4">
        <div className="grid gap-s3 sm:grid-cols-2">
          <Field
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            placeholder="Sunday Service"
            required
          />
          <SelectField
            label="Day"
            value={weekday}
            onChange={(e) => setWeekday(e.target.value)}
            options={WEEKDAYS.map((d, i) => ({ value: String(i), label: `Every ${d}` }))}
          />
        </div>

        <div className="space-y-s2">
          <span className="block text-sm font-semibold text-ink-2">Times</span>
          {slots.map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-s2 rounded border border-line-2 bg-surface-2 p-s2">
              <input
                aria-label="Name of this time"
                value={s.label}
                onChange={(e) => setSlot(i, { label: e.target.value.slice(0, 60) })}
                placeholder="English"
                className="h-10 min-w-[120px] flex-1 rounded border border-line-2 bg-surface px-s2 text-base outline-none focus:shadow-focus"
              />
              <input
                aria-label="Starts"
                type="time"
                value={s.starts_time}
                onChange={(e) => setSlot(i, { starts_time: e.target.value })}
                className="h-10 rounded border border-line-2 bg-surface px-s2 text-base outline-none focus:shadow-focus"
              />
              <span className="text-sm text-ink-2">to</span>
              <input
                aria-label="Ends"
                type="time"
                value={s.ends_time}
                onChange={(e) => setSlot(i, { ends_time: e.target.value })}
                className="h-10 rounded border border-line-2 bg-surface px-s2 text-base outline-none focus:shadow-focus"
              />
              {slots.length > 1 ? (
                <button
                  type="button"
                  onClick={() => setSlots((all) => all.filter((_, j) => j !== i))}
                  aria-label="Remove this time"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded text-ink-2 hover:bg-hover"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          ))}
          {!slotsOk ? (
            <p className="text-sm text-danger">Each time needs a name, and must end after it starts.</p>
          ) : null}
          {slots.length < 6 ? (
            <button
              type="button"
              onClick={() => {
                const last = slots[slots.length - 1]
                setSlots((all) => [
                  ...all,
                  { label: '', starts_time: last?.ends_time ?? '09:00', ends_time: '' },
                ])
              }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
            >
              <Plus className="h-4 w-4" aria-hidden /> Add another time
            </button>
          ) : null}
        </div>

        <div>
          <span className="text-sm font-semibold text-ink-2">Who is expected</span>
          <div className="mt-1 flex flex-wrap gap-s2">
            {(
              [
                ['church', 'Everyone', Users],
                ['chosen', 'Members who choose', UserCheck],
                ['group', 'A group', Users],
                ['filter', 'People who match…', Filter],
              ] as const
            ).map(([k, label, Icon]) => (
              <button
                key={k}
                type="button"
                aria-pressed={audience === k}
                onClick={() => setAudience(k)}
                className={cn(
                  'flex items-center gap-1.5 rounded-pill border px-s3 py-1.5 text-sm font-semibold transition-colors',
                  audience === k ? 'border-brand bg-brand-softer text-brand' : 'border-line-2 text-ink-2 hover:bg-hover',
                )}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {label}
              </button>
            ))}
          </div>
        </div>

        {audience === 'chosen' ? (
          <p className="text-sm text-ink-2">
            Members tap Attend regularly on This week. Only they — and their
            family — are expected, and only they are followed up when absent.
            Anybody may still come and check in.
          </p>
        ) : null}

        {audience === 'group' || audience === 'filter' ? (
          <SelectField
            label={audience === 'filter' ? 'Within' : 'Group'}
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            options={[
              { value: '', label: audience === 'filter' ? 'The whole church' : 'Choose a group' },
              ...(groups.data ?? []).map((g) => ({ value: g.id, label: g.name })),
            ]}
          />
        ) : null}

        {audience === 'filter' ? (
          <div className="space-y-s3 rounded border border-line-2 p-s3">
            <RuleBuilder churchId={churchId} value={rules} onChange={setRules} compact />
            <AudienceCount churchId={churchId} filter={rules} groupId={groupId || null} />
          </div>
        ) : null}

        {service ? (
          <p className="text-sm text-ink-2">Changes apply from the next service day.</p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
